/**
 * JSON-serialising wrapper over `sessionStorage` / `localStorage` — replaces `Storage` from `react-jhipster`.
 *
 * <p>The API is deliberately identical (`Storage.session.get/set/remove`, `Storage.local.…`) so the swap is a
 * one-line import change at every call site, and the read semantics are preserved exactly because the auth
 * token round-trips through here: values are written with `JSON.stringify`, so a token read back has to come
 * out unquoted. A missing key — or the literal string `'undefined'`, which is what an older `setItem(key,
 * undefined)` left behind — returns `defaultVal`, and a value that is not valid JSON is returned raw rather
 * than throwing, so a hand-edited or legacy entry cannot break the boot.
 */
type StorageArea = 'session' | 'local';

const area = (type: StorageArea): globalThis.Storage => (type === 'session' ? sessionStorage : localStorage);

const getItem =
  (type: StorageArea) =>
  <T = any>(key: string, defaultVal?: T): T => {
    const val = area(type).getItem(key);
    if (val === null || val === 'undefined') return defaultVal as T;
    try {
      return JSON.parse(val) as T;
    } catch {
      return val as unknown as T;
    }
  };

const setItem =
  (type: StorageArea) =>
  (key: string, value: any): void => {
    area(type).setItem(key, JSON.stringify(value));
  };

const removeItem =
  (type: StorageArea) =>
  (key: string): void => {
    area(type).removeItem(key);
  };

export const Storage = {
  session: {
    get: getItem('session'),
    set: setItem('session'),
    remove: removeItem('session'),
  },
  local: {
    get: getItem('local'),
    set: setItem('local'),
    remove: removeItem('local'),
  },
};

export default Storage;
